import { effect, inject, Injectable, Injector, signal } from '@angular/core';

import { STORAGE_KEYS } from '../../../shared/constants/storage-keys';
import {
  SPONSOR_AVOID_NO_RESTRICTIONS_VALUE,
  SPONSOR_COMPANY_ACTION_NONE_VALUE,
  SPONSOR_ONBOARDING_DEFAULT_COUNTRY,
  SPONSOR_SECTOR_OTHER_VALUE,
} from '../constants/sponsor-onboarding.constants';
import { SponsorAvoidPreference } from '../enums/sponsor-avoid-preference.enum';
import { SponsorCompanyAction } from '../enums/sponsor-company-action.enum';
import { SponsorOnboardingSector } from '../enums/sponsor-onboarding-sector.enum';
import { SponsorSportInterest } from '../enums/sponsor-sport-interest.enum';
import type { ISponsorAudienceStepData } from '../models/sponsor-audience-step-data.interface';
import type { ISponsorCampaignBudgetStepData } from '../models/sponsor-campaign-budget-step-data.interface';
import type { ISponsorCompanyActionsStepData } from '../models/sponsor-company-actions-step-data.interface';
import type { ISponsorExplorerDefaults } from '../models/sponsor-explorer-defaults.interface';
import type { ISponsorGeographyStepData } from '../models/sponsor-geography-step-data.interface';
import type { ISponsorGoalsStepData } from '../models/sponsor-goals-step-data.interface';
import type { ISponsorOnboardingDraft } from '../models/sponsor-onboarding-draft.interface';
import type { ISponsorPreferencesStepData } from '../models/sponsor-preferences-step-data.interface';
import type { ISponsorPrimaryLocationDraft } from '../models/sponsor-primary-location-draft.interface';
import type { ISponsorSectorStepData } from '../models/sponsor-sector-step-data.interface';

@Injectable({
  providedIn: 'root',
})
export class SponsorOnboardingDraftService {
  private readonly injector = inject(Injector);

  private readonly _draft = signal<ISponsorOnboardingDraft>(this.readDraftOrDefault());

  public readonly draft = this._draft.asReadonly();

  public constructor() {
    effect(
      () => {
        const serialized = JSON.stringify(this._draft());
        if (typeof sessionStorage === 'undefined') {
          return;
        }
        sessionStorage.setItem(STORAGE_KEYS.SPONSOR_ONBOARDING_DRAFT, serialized);
      },
      { injector: this.injector },
    );
  }

  public setDraft(draft: ISponsorOnboardingDraft): void {
    this._draft.set(draft);
  }

  public updateDraft(
    updater: (current: ISponsorOnboardingDraft) => ISponsorOnboardingDraft,
  ): void {
    this._draft.update(updater);
  }

  public patchGoals(partial: Partial<ISponsorGoalsStepData>): void {
    this._draft.update((current) => {
      const merged: ISponsorGoalsStepData = {
        ...current.goals,
        ...partial,
      };
      if (
        partial.primary_goal !== undefined &&
        merged.secondary_goal !== null &&
        merged.secondary_goal === merged.primary_goal
      ) {
        merged.secondary_goal = null;
      }
      if (
        partial.secondary_goal !== undefined &&
        merged.primary_goal !== null &&
        merged.secondary_goal === merged.primary_goal
      ) {
        merged.secondary_goal = null;
      }
      return {
        ...current,
        goals: merged,
      };
    });
  }

  public patchAudience(partial: Partial<ISponsorAudienceStepData>): void {
    this._draft.update((current) => {
      const merged: ISponsorAudienceStepData = {
        ...current.audience,
        ...partial,
      };
      if (
        partial.primary_audience !== undefined &&
        merged.secondary_audience !== null &&
        merged.secondary_audience === merged.primary_audience
      ) {
        merged.secondary_audience = null;
      }
      if (
        partial.secondary_audience !== undefined &&
        merged.primary_audience !== null &&
        merged.secondary_audience === merged.primary_audience
      ) {
        merged.secondary_audience = null;
      }
      return {
        ...current,
        audience: merged,
      };
    });
  }

  public patchGeography(partial: Partial<ISponsorGeographyStepData>): void {
    this._draft.update((current) => {
      const mergedPrimaryLocation = {
        ...current.geography.primary_location,
        ...(partial.primary_location ?? {}),
      };
      return {
        ...current,
        geography: {
          ...current.geography,
          ...partial,
          primary_location: mergedPrimaryLocation,
        },
      };
    });
  }

  public patchSector(partial: Partial<ISponsorSectorStepData>, options?: { replace?: boolean }): void {
    this._draft.update((current) => {
      const merged: ISponsorSectorStepData = {
        ...current.sector,
        ...partial,
      };
      if (options?.replace) {
        merged.sector = partial.sector ?? null;
      }
      if ((merged.sector ?? null) !== SPONSOR_SECTOR_OTHER_VALUE) {
        merged.sector_other = null;
      }
      return {
        ...current,
        sector: merged,
      };
    });
  }

  public patchCampaignBudget(partial: Partial<ISponsorCampaignBudgetStepData>): void {
    this._draft.update((current) => ({
      ...current,
      campaign_budget: {
        ...current.campaign_budget,
        ...partial,
      },
    }));
  }

  public patchCompanyActions(partial: Partial<ISponsorCompanyActionsStepData>): void {
    this._draft.update((current) => ({
      ...current,
      company_actions: {
        company_actions: this.normalizeCompanyActions(
          partial.company_actions ?? current.company_actions.company_actions,
        ),
      },
    }));
  }

  public patchPreferences(partial: Partial<ISponsorPreferencesStepData>): void {
    this._draft.update((current) => ({
      ...current,
      preferences: {
        sports_interest: this.normalizeSportsInterest(
          partial.sports_interest ?? current.preferences.sports_interest,
        ),
        avoid_preferences: this.normalizeAvoidPreferences(
          partial.avoid_preferences ?? current.preferences.avoid_preferences,
        ),
      },
    }));
  }

  public restoreDraft(): void {
    this._draft.set(this.readDraftOrDefault());
  }

  public clearDraft(): void {
    this.clearDraftStorage();
    this._draft.set(this.buildDefaultDraft());
  }

  public readExplorerDefaults(): ISponsorExplorerDefaults | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    const raw = sessionStorage.getItem(STORAGE_KEYS.SPONSOR_APPLY_EXPLORER_DEFAULTS);
    if (!raw) {
      return null;
    }
    try {
      const parsedUnknown: unknown = JSON.parse(raw);
      if (!parsedUnknown || typeof parsedUnknown !== 'object') {
        return null;
      }
      return parsedUnknown as ISponsorExplorerDefaults;
    } catch {
      return null;
    }
  }

  public clearExplorerDefaults(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.removeItem(STORAGE_KEYS.SPONSOR_APPLY_EXPLORER_DEFAULTS);
  }

  public storeExplorerDefaultsBeforeCompletion(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    const draft = this._draft();
    const defaults: ISponsorExplorerDefaults = {
      primary_location: { ...draft.geography.primary_location },
      geographic_radius: draft.geography.geographic_radius,
      sports_interest: [...draft.preferences.sports_interest],
    };
    sessionStorage.setItem(STORAGE_KEYS.SPONSOR_APPLY_EXPLORER_DEFAULTS, JSON.stringify(defaults));
  }

  public clearDraftAfterCompletion(): void {
    this.clearDraftStorage();
    this._draft.set(this.buildDefaultDraft());
  }

  public readDraftOrDefault(): ISponsorOnboardingDraft {
    if (typeof sessionStorage === 'undefined') {
      return this.buildDefaultDraft();
    }
    const raw = sessionStorage.getItem(STORAGE_KEYS.SPONSOR_ONBOARDING_DRAFT);
    if (!raw) {
      return this.buildDefaultDraft();
    }
    try {
      const parsedUnknown: unknown = JSON.parse(raw);
      if (!this.isDraftShape(parsedUnknown)) {
        this.clearDraftStorage();
        return this.buildDefaultDraft();
      }
      return this.mergeWithDefaults(parsedUnknown);
    } catch {
      this.clearDraftStorage();
      return this.buildDefaultDraft();
    }
  }

  public mergeWithDefaults(value: ISponsorOnboardingDraft): ISponsorOnboardingDraft {
    const fallback = this.buildDefaultDraft();
    return {
      goals: {
        ...fallback.goals,
        ...(value.goals ?? {}),
      },
      audience: {
        ...fallback.audience,
        ...(value.audience ?? {}),
      },
      geography: this.mergeGeographyWithDefaults(value.geography, fallback.geography),
      sector: {
        ...fallback.sector,
        ...(value.sector ?? {}),
        sector_other: value.sector?.sector_other ?? null,
      },
      campaign_budget: {
        ...fallback.campaign_budget,
        ...(value.campaign_budget ?? {}),
      },
      company_actions: {
        company_actions: this.normalizeCompanyActions(
          value.company_actions?.company_actions ?? fallback.company_actions.company_actions,
        ),
      },
      preferences: {
        sports_interest: this.normalizeSportsInterest(
          value.preferences?.sports_interest ?? fallback.preferences.sports_interest,
        ),
        avoid_preferences: this.normalizeAvoidPreferences(
          value.preferences?.avoid_preferences ?? fallback.preferences.avoid_preferences,
        ),
      },
    };
  }

  public mergeGeographyWithDefaults(
    incoming: ISponsorGeographyStepData | undefined,
    fallback: ISponsorGeographyStepData,
  ): ISponsorGeographyStepData {
    if (!incoming || typeof incoming !== 'object') {
      return fallback;
    }
    const primaryIncoming = incoming.primary_location;
    const merged: ISponsorGeographyStepData = {
      geographic_radius: incoming.geographic_radius ?? null,
      primary_location: {
        ...fallback.primary_location,
        ...(primaryIncoming && typeof primaryIncoming === 'object' ? primaryIncoming : {}),
        formatted_address:
          primaryIncoming?.formatted_address ?? fallback.primary_location.formatted_address,
        latitude: primaryIncoming?.latitude ?? fallback.primary_location.latitude,
        longitude: primaryIncoming?.longitude ?? fallback.primary_location.longitude,
      },
    };
    if (!merged.primary_location.country.trim()) {
      merged.primary_location.country = SPONSOR_ONBOARDING_DEFAULT_COUNTRY;
    }
    return merged;
  }

  public buildDefaultDraft(): ISponsorOnboardingDraft {
    return {
      goals: {
        company_name: '',
        primary_goal: null,
        secondary_goal: null,
      },
      audience: {
        primary_audience: null,
        secondary_audience: null,
      },
      geography: {
        primary_location: this.buildEmptyPrimaryLocation(),
        geographic_radius: null,
      },
      sector: {
        sector: null as SponsorOnboardingSector | null,
        sector_other: null,
      },
      campaign_budget: {
        budget_range: null,
        campaign_duration: null,
      },
      company_actions: {
        company_actions: [],
      },
      preferences: {
        sports_interest: [],
        avoid_preferences: [],
      },
    };
  }

  public normalizeCompanyActions(actions: SponsorCompanyAction[]): SponsorCompanyAction[] {
    const allowedValues = new Set(Object.values(SponsorCompanyAction));
    const unique = [...new Set(actions.filter((action) => allowedValues.has(action)))];
    if (unique.includes(SPONSOR_COMPANY_ACTION_NONE_VALUE)) {
      return [SPONSOR_COMPANY_ACTION_NONE_VALUE];
    }
    return unique;
  }

  public normalizeSportsInterest(interests: SponsorSportInterest[]): SponsorSportInterest[] {
    const allowedValues = new Set(Object.values(SponsorSportInterest));
    return [...new Set(interests.filter((interest) => allowedValues.has(interest)))];
  }

  public normalizeAvoidPreferences(
    preferences: SponsorAvoidPreference[],
  ): SponsorAvoidPreference[] {
    const allowedValues = new Set(Object.values(SponsorAvoidPreference));
    const unique = [...new Set(preferences.filter((preference) => allowedValues.has(preference)))];
    if (unique.includes(SPONSOR_AVOID_NO_RESTRICTIONS_VALUE)) {
      return [SPONSOR_AVOID_NO_RESTRICTIONS_VALUE];
    }
    return unique;
  }

  public toNullableTrimmedString(value: string | null | undefined): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  public buildEmptyPrimaryLocation(): ISponsorPrimaryLocationDraft {
    return {
      formatted_address: null,
      address: '',
      city: '',
      province: '',
      region: null,
      postal_code: null,
      country: SPONSOR_ONBOARDING_DEFAULT_COUNTRY,
      latitude: null,
      longitude: null,
    };
  }

  public isDraftShape(value: unknown): value is ISponsorOnboardingDraft {
    if (!value || typeof value !== 'object') {
      return false;
    }
    const candidate = value as Record<string, unknown>;
    const goalsUnknown = candidate['goals'];
    return Boolean(goalsUnknown && typeof goalsUnknown === 'object');
  }

  public clearDraftStorage(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.removeItem(STORAGE_KEYS.SPONSOR_ONBOARDING_DRAFT);
  }
}
